import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface Organization {
  id: string;
  name: string;
  color: string;
  type: string;
}

// Function to convert hex to HSL
const hexToHsl = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Function to apply organization theme - exported for use in other components
export const applyOrganizationTheme = (org: Organization) => {
  const root = document.documentElement;
  
  if (org.type === 'vodafone') {
    root.style.setProperty('--primary', '0 100% 45%'); // Vodafone Red
    root.style.setProperty('--accent', '0 85% 50%');
    root.style.setProperty('--primary-glow', '0 100% 55%');
    root.style.setProperty('--secondary', '0 90% 40%');
  } else {
    const hslColor = hexToHsl(org.color);
    
    // Apply the selected color as primary
    root.style.setProperty('--primary', hslColor);
    root.style.setProperty('--accent', hslColor);
    
    // Create a lighter version for glow effects
    const [h, s, l] = hslColor.split(' ');
    const lighterL = Math.min(parseInt(l.replace('%', '')) + 10, 90);
    root.style.setProperty('--primary-glow', `${h} ${s} ${lighterL}%`);
  }
};

export const useOrganizationTheme = () => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrganizationTheme = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('Loading organization theme for user:', user.id);
        
        // Get user's organization from profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !profile?.organization_id) {
          console.log('No organization found for user');
          setLoading(false);
          return;
        }

        // Get organization details
        const { data: org, error: orgError } = await supabase
          .from('organizations')
          .select('id, name, color, type')
          .eq('id', profile.organization_id)
          .single();

        if (orgError) {
          console.error('Error loading organization:', orgError);
          setLoading(false);
          return;
        }

        console.log('Organization loaded:', org);
        setOrganization(org);
        
        // Apply the organization theme
        if (org) {
          applyOrganizationTheme(org);
        }
      } catch (error) {
        console.error('Error in loadOrganizationTheme:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizationTheme();
  }, [user?.id]); // Only depend on user.id to prevent infinite loops

  return {
    organization,
    loading,
    applyOrganizationTheme
  };
};